import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { FARSI_ENABLED, SUPPORTED_I18N_LANGUAGES } from '../config/languages';

// Import translations directly for immediate availability
import enCommon from '../locales/en/common.json';
import enAuth from '../locales/en/auth.json';
import enDashboard from '../locales/en/dashboard.json';
import enRations from '../locales/en/rations.json';
import enInventory from '../locales/en/inventory.json';
import enNotifications from '../locales/en/notifications.json';
import enValidation from '../locales/en/validation.json';
import enLanding from '../locales/en/landing.json';
import enFiles from '../locales/en/files.json';
import enRecords from '../locales/en/records.json';
import enHelp from '../locales/en/help.json';
import enSupportTickets from '../locales/en/supportTickets.json';

import frCommon from '../locales/fr/common.json';
import frAuth from '../locales/fr/auth.json';
import frDashboard from '../locales/fr/dashboard.json';
import frRations from '../locales/fr/rations.json';
import frInventory from '../locales/fr/inventory.json';
import frNotifications from '../locales/fr/notifications.json';
import frValidation from '../locales/fr/validation.json';
import frLanding from '../locales/fr/landing.json';
import frFiles from '../locales/fr/files.json';
import frRecords from '../locales/fr/records.json';
import frHelp from '../locales/fr/help.json';

import esCommon from '../locales/es/common.json';
import esAuth from '../locales/es/auth.json';
import esDashboard from '../locales/es/dashboard.json';
import esRations from '../locales/es/rations.json';
import esInventory from '../locales/es/inventory.json';
import esNotifications from '../locales/es/notifications.json';
import esValidation from '../locales/es/validation.json';
import esLanding from '../locales/es/landing.json';
import esFiles from '../locales/es/files.json';
import esRecords from '../locales/es/records.json';
import esHelp from '../locales/es/help.json';

// Farsi bundles kept for re-enable via FARSI_ENABLED in config/languages.ts
import faCommon from '../locales/fa/common.json';
import faAuth from '../locales/fa/auth.json';
import faDashboard from '../locales/fa/dashboard.json';
import faRations from '../locales/fa/rations.json';
import faInventory from '../locales/fa/inventory.json';
import faNotifications from '../locales/fa/notifications.json';
import faValidation from '../locales/fa/validation.json';
import faLanding from '../locales/fa/landing.json';
import faFiles from '../locales/fa/files.json';
import faRecords from '../locales/fa/records.json';
import faHelp from '../locales/fa/help.json';

const faResources = FARSI_ENABLED
  ? {
      common: faCommon,
      auth: faAuth,
      dashboard: faDashboard,
      rations: faRations,
      inventory: faInventory,
      notifications: faNotifications,
      validation: faValidation,
      landing: faLanding,
      files: faFiles,
      records: faRecords,
      help: faHelp,
      supportTickets: enSupportTickets,
    }
  : undefined;

const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    dashboard: enDashboard,
    rations: enRations,
    inventory: enInventory,
    notifications: enNotifications,
    validation: enValidation,
    landing: enLanding,
    files: enFiles,
    records: enRecords,
    help: enHelp,
    supportTickets: enSupportTickets,
  },
  fr: {
    common: frCommon,
    auth: frAuth,
    dashboard: frDashboard,
    rations: frRations,
    inventory: frInventory,
    notifications: frNotifications,
    validation: frValidation,
    landing: frLanding,
    files: frFiles,
    records: frRecords,
    help: frHelp,
    supportTickets: enSupportTickets,
  },
  es: {
    common: esCommon,
    auth: esAuth,
    dashboard: esDashboard,
    rations: esRations,
    inventory: esInventory,
    notifications: esNotifications,
    validation: esValidation,
    landing: esLanding,
    files: esFiles,
    records: esRecords,
    help: esHelp,
    supportTickets: enSupportTickets,
  },
  ...(faResources ? { fa: faResources } : {}),
};

i18n
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_I18N_LANGUAGES],
    debug: import.meta.env.DEV && import.meta.env.VITE_I18N_DEBUG === 'true',
    
    // Language detection options
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },

    ns: [
      'common',
      'auth',
      'dashboard',
      'rations',
      'inventory',
      'notifications',
      'validation',
      'landing',
      'files',
      'records',
    ],
    defaultNS: 'common',

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
    },
  })
  .then(() => {
    const lang = i18n.language?.split('-')[0];
    if (!FARSI_ENABLED && lang === 'fa') {
      void i18n.changeLanguage('en');
      localStorage.setItem('i18nextLng', 'en');
    }
  });

export default i18n;

